import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, apiFormData, getErrorMessage } from '../utils/api';
import { showToast } from '../utils/toast';
import { EssayNode, ReworkNote } from './useEssayApi';

export type GenerationStateName = 'not-started' | 'running' | 'done' | 'failed';

export interface GenerationState {
  state: GenerationStateName;
  startedAt?: string;
  error?: string;
}

export type ReportSourceStatus = 'submitted' | 'draft' | 'missing';

export interface ReportSource {
  year: number;
  month: number;
  status: ReportSourceStatus;
}

export interface QuarterlyReport {
  _id: string;
  user: string;
  year: number;
  quarter: number;
  status: 'draft' | 'submitted';
  submittedAt?: string | null;
  // Пометка «вернули на доработку». Снимается следующей сдачей.
  returnedForRework?: ReworkNote | null;
  items: EssayNode[];
  generatedAt?: string | null;
  sources: ReportSource[];
  generation: GenerationState;
  createdAt: string;
  updatedAt: string;
}

export interface QuarterlyResponse {
  report: QuarterlyReport;
  canEdit: boolean;
  /** Может ли смотрящий вернуть отчёт автору. */
  canReturn: boolean;
  /** По отчёту есть оценки: текст заморожен, вернуть или расчерновить нельзя. */
  isScored: boolean;
}

/** Одна строка сводки за квартал: человек + состояние его отчёта. */
export interface QuarterlySummaryRow {
  userId: string;
  name: string;
  position?: string;
  reportId: string | null;
  status: 'draft' | 'submitted' | 'none';
  updatedAt: string | null;
  submittedAt: string | null;
  returnedAt: string | null;
  isEmpty: boolean;
}

/** Столько же, сколько сервер считает сборку мёртвой (STALE_GENERATION_MS). */
const STALE_MS = 3 * 60 * 1000;
const POLL_MS = 2000;

export const isGenerationStale = (startedAt?: string): boolean =>
  !!startedAt && Date.now() - new Date(startedAt).getTime() > STALE_MS;

/**
 * Как часто перечитывать документ. `false` останавливает опрос: и когда сборка
 * закончилась, и когда она висит дольше потолка — процесс мог умереть в момент
 * генерации, и вечный спиннер человеку не помогает.
 */
export const pollInterval = (generation?: GenerationState): number | false =>
  generation?.state === 'running' && !isGenerationStale(generation.startedAt) ? POLL_MS : false;

export const useMyQuarterlyReports = (enabled = true) =>
  useQuery({
    queryKey: ['quarterly', 'mine'],
    queryFn: async (): Promise<QuarterlyReport[]> => api('/quarterly-reports/mine'),
    enabled,
  });

export const useQuarterlyReport = (id?: string) =>
  useQuery({
    queryKey: ['quarterly', id],
    queryFn: async (): Promise<QuarterlyResponse> => api(`/quarterly-reports/${id}`),
    enabled: !!id,
    refetchInterval: (query) => pollInterval(query.state.data?.report.generation),
  });

export const saveQuarterlyItems = async (id: string, items: EssayNode[]): Promise<unknown> =>
  api(`/quarterly-reports/${id}`, { method: 'PUT', body: { items } });

// Пустой отчёт за квартал — писать руками, без сборки. Уже есть — вернётся он же.
export const useCreateEmptyQuarterly = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { year: number; quarter: number }): Promise<{ id: string }> =>
      api(`/quarterly-reports/${p.year}/${p.quarter}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quarterly'] });
    },
  });
};

/** Удаление отчёта. Сервер откажет по сданному и по оценённому — тост об этом скажет. */
export const useDeleteQuarterly = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      api(`/quarterly-reports/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quarterly'] }),
  });
};

export const useGenerateQuarterly = () => {
  const qc = useQueryClient();
  return useMutation({
    // Файлы — приложенные Word-эссе: без них обычный запрос, с ними multipart.
    mutationFn: async (p: {
      year: number;
      quarter: number;
      files?: File[];
    }): Promise<{ id: string; isTruncated?: boolean }> => {
      const path = `/quarterly-reports/${p.year}/${p.quarter}/generate`;
      if (!p.files?.length) return api(path, { method: 'POST' });
      const body = new FormData();
      p.files.forEach((f) => body.append('files', f));
      return apiFormData(path, { method: 'POST', body });
    },
    onSuccess: (_, { files }) => {
      trackEvent(AnalyticsEvents.QUARTERLY_REPORT_GENERATED, {
        source: files?.length ? 'word' : 'essays',
      });
      qc.invalidateQueries({ queryKey: ['quarterly'] });
    },
  });
};

// Смена статуса. Роуты идемпотентны, поэтому повторный клик безопасен.
const useStatusMutation = (action: 'submit' | 'unsubmit') => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api(`/quarterly-reports/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      if (action === 'submit') trackEvent(AnalyticsEvents.QUARTERLY_REPORT_SUBMITTED);
      qc.invalidateQueries({ queryKey: ['quarterly'] });
    },
  });
};

export const useSubmitQuarterly = () => useStatusMutation('submit');
export const useUnsubmitQuarterly = () => useStatusMutation('unsubmit');

/** Возврат автору на доработку: обязательный комментарий уходит ему письмом. */
export const useReturnQuarterly = () => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment: string }) =>
      api(`/quarterly-reports/${id}/return`, { method: 'POST', body: { comment } }),
    onSuccess: () => {
      trackEvent(AnalyticsEvents.DOCUMENT_RETURNED_FOR_REWORK, { kind: 'quarterly' });
      return qc.invalidateQueries({ queryKey: ['quarterly'] });
    },
    onError: (error) =>
      showToast({
        title: t('common.error'),
        message: getErrorMessage(error, t('essays.saveError')),
        color: 'red',
      }),
  });
};

export const useQuarterlySummary = (year: number, quarter: number, enabled: boolean) =>
  useQuery({
    queryKey: ['quarterly', 'summary', year, quarter],
    queryFn: async (): Promise<QuarterlySummaryRow[]> =>
      api(`/quarterly-reports/manage/summary?year=${year}&quarter=${quarter}`),
    enabled,
  });
